import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, ShieldCheck, Flame } from 'lucide-react';

const ONBOARDING_SHOWN_KEY = 'onboarding_shown';

interface OnboardingProps {
  onComplete: () => void;
}

const slides = [
  {
    title: '중고도 좋지만,\n이왕이면 새 상품!',
    description: '새상품이지만 중고가격만큼 할인된\n놀라운 상품들을 만나보세요.',
    icon: ShoppingBag,
    colors: ['#6366f1', '#a855f7'],
  },
  {
    title: '매장에서 보던\n그 상품 그대로!',
    description: "전시만 되어있던 '신상급' 컨디션,\n꼼꼼하게 검수하여 준비했습니다.",
    icon: ShieldCheck,
    colors: ['#f59e0b', '#ef4444'],
  },
  {
    title: '진상마켓에서\n갓성비 득템하세요!',
    description: '이제 거품 뺀 가격으로\n스마트한 쇼핑을 시작할 시간입니다.',
    icon: Flame,
    colors: ['#10b981', '#3b82f6'],
  },
];

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      if (dontShowAgain) {
        localStorage.setItem(ONBOARDING_SHOWN_KEY, 'true');
      }
      onComplete();
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance && currentSlide < slides.length - 1) setCurrentSlide((c) => c + 1);
    if (distance < -minSwipeDistance && currentSlide > 0) setCurrentSlide((c) => c - 1);
  };

  const slide = slides[currentSlide];
  const Icon = slide.icon;
  const isLast = currentSlide === slides.length - 1;

  return (
    <div
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${slide.colors[0]} 0%, ${slide.colors[1]} 100%)`,
      }}
    >
      <div className="relative z-10 flex flex-col h-full">
        <header className="flex items-center justify-between px-6 pt-6">
          <span className="text-sm font-black tracking-widest text-white/80">JINSANG MARKET</span>
          <button
            type="button"
            onClick={handleSkip}
            className="py-2 px-4 rounded-full bg-white/20 text-white text-sm font-semibold hover:bg-white/30 transition-colors"
          >
            건너뛰기
          </button>
        </header>

        <div
          className="flex-1 flex flex-col items-center justify-center px-10"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center text-center"
          >
            <div className="w-28 h-28 rounded-3xl bg-white/90 flex items-center justify-center shadow-xl mb-10">
              <Icon className="w-14 h-14" style={{ color: slide.colors[0] }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white whitespace-pre-line leading-tight mb-4">
              {slide.title}
            </h2>
            <p className="text-base text-white/90 whitespace-pre-line leading-relaxed">
              {slide.description}
            </p>
          </motion.div>
        </div>

        <footer className="px-6 pb-10">
          <div className="flex justify-center gap-2 mb-6">
            {slides.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentSlide(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentSlide ? 'w-6 bg-white' : 'w-2 bg-white/30'
                }`}
                aria-label={`슬라이드 ${index + 1}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={nextSlide}
            className="w-full h-14 rounded-full bg-white text-gray-800 font-bold text-lg shadow-lg hover:opacity-95 active:scale-[0.98] transition"
          >
            {isLast ? '지금 바로 시작하기' : '계속하기'}
          </button>
          <label className="flex items-center justify-center gap-2 mt-5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-4 h-4 rounded border-2 border-white accent-white"
            />
            <span className="text-white text-sm font-medium">오늘 하루 보지 않기</span>
          </label>
        </footer>
      </div>
    </div>
  );
};

export default Onboarding;
